import {FC, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {
  ClientAddress,
  ClientEntity,
  clientAddressIsPrimary,
  isClientEntityOperational,
  isClientEntityComplex
} from "@common-core/coat-operational-hierarchy-appsync-model";
import {Badge} from "@interstate/components/Badge";
import {LinkChip} from "@interstate/components/Chip";
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ShieldSolidIcon
} from "@interstate/components/Icons";
import {useInterstateToken} from "@interstate/components/InterstateThemeProvider";
import {useNavigation} from "../../../navigation";
import {BadgeBar} from "../../../badges";
import {EntitySourceType} from "../../EntitySourceType";
import {OperationalEntityPopover} from "../../hierarchy";
import "../../hierarchy/HierarchyNodeLabel.scss";

interface ClientHierarchyNodeMetadata {
  operational: boolean;
  complex: boolean;
}

export interface ClientHierarchyNodeLabelProps {
  node: ClientEntity;
}

export const ClientHierarchyNodeLabel: FC<ClientHierarchyNodeLabelProps> = ({
  node
}) => {
  const {t} = useTranslation();
  const token = useInterstateToken();
  const {navigateToEntity} = useNavigation();

  const addressToDisplay = useMemo<ClientAddress | undefined>(() => {
    if (node.addresses) {
      return node.addresses.find(clientAddressIsPrimary) || node.addresses[0];
    }
  }, [node]);

  const {operational, complex} = useMemo<ClientHierarchyNodeMetadata>(() => {
    return {
      operational: isClientEntityOperational(node),
      complex: isClientEntityComplex(node)
    };
  }, [node]);

  return (
    <div
      id={`client-hierarchy-node-label-${node.caId}`}
      data-testid={`client-hierarchy-node-label-${node.caId}`}
      className={"client-hierarchy-node-label hierarchy-node-label"}>
      <div className="organization-info">
        <div className="dba-name">{node.custName}</div>
        <div className="address-line">{formatAddress(addressToDisplay)}</div>
      </div>
      <BadgeBar
        onClick={(event: Event) => {
          event.stopPropagation();
        }}>
        {complex && (
          <Badge
            id={`client-hierarchy-complex-badge-${node.caId}`}
            data-testid={`client-hierarchy-complex-badge-${node.caId}`}
            className={"client-hierarchy-badge client-hierarchy-complex-badge"}
            variant={"info"}
            startIcon={<ShieldSolidIcon />}>
            <span className={"client-hierarchy-badge-label"}>
              {t("client-hierarchy.badges.complex")}
            </span>
          </Badge>
        )}
        {operational && (
          <OperationalEntityPopover
            operationalEntities={node.mappedOperationalEntities!}
            caId={node.caId}>
            <Badge
              id={`client-hierarchy-operational-badge-${node.caId}`}
              data-testid={`client-hierarchy-operational-badge-${node.caId}`}
              className={
                "client-hierarchy-badge client-hierarchy-operational-badge"
              }
              variant={"transparent"}
              startIcon={
                <CheckCircleIcon
                  color={token("sem.color.on-surface.success.muted")}
                />
              }>
              <span className={"client-hierarchy-badge-label"}>
                {t("client-hierarchy.badges.operational")}
              </span>
            </Badge>
          </OperationalEntityPopover>
        )}
        <LinkChip
          data-testid={`client-overview-link-${node.caId}`}
          icon={<ArrowTopRightOnSquareIcon />}
          iconPosition={"left"}
          label={t("client-hierarchy.hierarchy-view.overview-link")}
          onClick={(event: Event) => {
            event.stopPropagation();
            navigateToEntity(node.caId, EntitySourceType.CLIENT_HIERARCHY);
          }}
        />
      </BadgeBar>
    </div>
  );
};

/**
 * Returns a formatted address string for a given address object.
 * @param address The address object to format
 */
const formatAddress = (address?: ClientAddress) =>
  address
    ? `${address.addrLine1}, ${address.addrLine2 ? address.addrLine2 + ", " : ""}${address.city}, ${address.provinceCd}`
    : "";
